import { useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

type Placement = "top" | "bottom";

export function PortalLightTooltip({
  label,
  children,
  placement = "top",
}: {
  label: string;
  children: ReactNode;
  placement?: Placement;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    if (placement === "bottom") {
      setPos({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
    } else {
      setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
    }
  };
  const hide = () => setPos(null);

  const isBottom = placement === "bottom";
  const transform = isBottom ? "translate(-50%, 0)" : "translate(-50%, -100%)";
  // Arrow sits on the side of the bubble closest to the trigger.
  const arrowClass = isBottom
    ? "absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-background border-l border-t border-input rotate-45 z-10"
    : "absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-background border-r border-b border-input rotate-45 z-10";

  return (
    <span ref={ref} onMouseEnter={show} onMouseLeave={hide} className="relative inline-flex shrink-0">
      {children}
      {pos && createPortal(
        <span
          className="fixed pointer-events-none z-[9999] animate-in fade-in zoom-in-95 duration-150"
          style={{ top: pos.top, left: pos.left, transform }}
        >
          <span className={arrowClass} />
          <span className="relative block bg-background text-foreground text-[13px] font-medium px-2.5 py-1 rounded-md border border-input shadow-xs whitespace-nowrap">
            {label}
          </span>
        </span>,
        document.body
      )}
    </span>
  );
}
